import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { NotFoundComponent } from './modules/not-found/not-found.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Redirect empty path to '/example'
    {path: '', pathMatch : 'full', redirectTo: 'home'},


    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {path: 'signed-in-redirect', pathMatch : 'full', redirectTo: 'home'},

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
    
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'verify', loadComponent: () => import('app/modules/auth/confirmation-required/confirmation-required.component').then(comp => comp.AuthConfirmationRequiredComponent)},
            {path: 'forgot-password', loadComponent: () => import('app/modules/auth/forgot-password/forgot-password.component').then(comp => comp.AuthForgotPasswordComponent)},
            {path: 'reset', loadComponent: () => import('app/modules/auth/reset-password/reset-password.component').then(comp => comp.AuthResetPasswordComponent)},
            {path: 'sign-in', loadComponent: () => import('app/modules/auth/sign-in/sign-in.component').then(comp => comp.AuthSignInComponent)},
            {path: 'sign-up', loadComponent: () => import('app/modules/auth/sign-up/sign-up.component').then(comp => comp.AuthSignUpComponent)},
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'sign-out', loadComponent: () => import('app/modules/auth/sign-out/sign-out.component').then(comp => comp.AuthSignOutComponent)},
            //{path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/')}
        ]
    },

    {
        path: '',
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'terms-and-conditions', loadComponent: () => import('app/modules/terms-and-conditions/terms-and-conditions.component').then(comp => comp.TermsAndConditionsComponent)},
            {
                path: 'contact-us', loadComponent: () => import('app/modules/contact-us/contact-us.component').then(comp => comp.ContactUsComponent),
                data: {
                    title: 'Contact Supersonic - Get in Touch with South Africa’s Leading ISP',
                    description: 'Reach out to Supersonic for any inquiries, support requests, or information about our internet services. Our dedicated team is ready to assist you with all your connectivity needs.',
                    keywords: 'contact Supersonic, ISP contact information, South Africa internet support, customer inquiries, Supersonic contact',

                    ogTitle: 'Contact Supersonic - Get in Touch with South Africa’s Leading ISP',
                    ogDescription: 'Reach out to Supersonic for any inquiries, support requests, or information about our internet services. Our dedicated team is ready to assist you with all your connectivity needs.',
                    ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    ogUrl: 'https://supersonic.co.za/contact-us',
                    ogType: 'website',

                    twCard: 'summary_large_image',
                    twTitle: 'Contact Supersonic - Get in Touch with South Africa’s Leading ISP',
                    twDescription: 'Reach out to Supersonic for any inquiries, support requests, or information about our internet services. Our dedicated team is ready to assist you with all your connectivity needs.',
                    twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',

                    canonical: 'https://supersonic.co.za/contact-us'
                }
            },
            {                
                path: 'contact-us/complaints', loadComponent: () => import('app/modules/contact-us/complaints/complaints.component').then(comp => comp.ComplaintsComponent),
                data: {
                    title: 'Supersonic Complaints Handling Procedure',
                    description: 'Experiencing issues with your Supersonic internet service? If you haven’t received a resolution via ClientZone, use our official Complaints Handling Procedure to submit a formal complaint. Fast, fair, and transparent support for South African customers.',
                    keywords: 'Supersonic complaints procedure, Supersonic internet complaint, Supersonic ISP support, Supersonic ClientZone issue, Submit ISP complaint South Africa, Internet service complaint Supersonic, unresolved ticket, Supersonic customer service complaint, Fibre internet complaint South Africa',

                    ogTitle: 'Supersonic Complaints Handling Procedure',
                    ogDescription: 'Experiencing issues with your Supersonic internet service? If you haven’t received a resolution via ClientZone, use our official Complaints Handling Procedure to submit a formal complaint. Fast, fair, and transparent support for South African customers.',
                    ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    ogUrl: 'https://supersonic.co.za/contact-us/complaints',
                    ogType: 'website',

                    twCard: 'summary_large_image',
                    twTitle: 'Supersonic Complaints Handling Procedure',
                    twDescription: 'Experiencing issues with your Supersonic internet service? If you haven’t received a resolution via ClientZone, use our official Complaints Handling Procedure to submit a formal complaint. Fast, fair, and transparent support for South African customers.',
                    twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',

                    canonical: 'https://supersonic.co.za/contact-us/complaints'
                }
            },
            {
                path: 'about-us', loadComponent: () => import('app/modules/about-us/about-us.component').then(comp => comp.AboutUsComponent),
                data: {
                    title: 'About Supersonic - Leading ISP in South Africa | Our Mission & Values',
                    description: 'Learn more about Supersonic, South Africa\'s premier ISP. Discover our mission, values, and commitment to providing high-quality internet services.',
                    keywords: 'about Supersonic, ISP mission, South Africa internet provider, company values, Supersonic team',

                    ogTitle: 'About Supersonic - Leading ISP in South Africa | Our Mission & Values',
                    ogDescription: 'Learn more about Supersonic, South Africa\'s premier ISP. Discover our mission, values, and commitment to providing high-quality internet services.',
                    ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    ogUrl: 'https://supersonic.co.za/about-us',
                    ogType: 'website',

                    twCard: 'summary_large_image',
                    twTitle: 'About Supersonic - Leading ISP in South Africa | Our Mission & Values',
                    twDescription: 'Learn more about Supersonic, South Africa\'s premier ISP. Discover our mission, values, and commitment to providing high-quality internet services.',
                    twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',

                    canonical: 'https://supersonic.co.za/about-us'
                }
            },
            // {path: 'faq', loadComponent: () => import('app/modules/faq/faq.component').then(comp => comp.FaqComponent)},
            {
                path: 'product-overview', loadChildren: () => import('app/modules/catalog/catalog.routes'),
                data: {
                    title: 'Supersonic Internet Products - Overview of Our High-Speed Connectivity Solutions',
                    description: 'Explore Supersonic’s range of internet products, including fibre, 5G, fixed LTE, and more. Find the perfect connectivity solution for your needs with our comprehensive overview.',
                    keywords: 'internet products South Africa, Supersonic product overview, fibre internet, 5G internet, fixed LTE, connectivity solutions',

                    ogTitle: 'Supersonic Internet Products - Overview of Our High-Speed Connectivity Solutions',
                    ogDescription: 'Explore Supersonic’s range of internet products, including fibre, 5G, fixed LTE, and more. Find the perfect connectivity solution for your needs with our comprehensive overview.',
                    ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    ogUrl: 'https://supersonic.co.za/product-overview',
                    ogType: 'website',

                    twCard: 'summary_large_image',
                    twTitle: 'Supersonic Internet Products - Overview of Our High-Speed Connectivity Solutions',
                    twDescription: 'Explore Supersonic’s range of internet products, including fibre, 5G, fixed LTE, and more. Find the perfect connectivity solution for your needs with our comprehensive overview.',
                    twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',

                    canonical: 'https://supersonic.co.za/product-overview'
                }
            },
            {path: 'products', loadChildren: () => import('app/modules/products/products.routes')},
            {
                path: 'support', loadComponent: () => import('app/modules/support/support.component').then(comp => comp.SupportComponent),
                data: {
                    title: 'Supersonic Support - Help & Resources for Your Internet Services in South Africa',
                    description: 'Get comprehensive support with Supersonic. Access FAQs, troubleshooting tips, and contact options for assistance with your internet services. We’re here to help you with all your connectivity needs.',
                    keywords: 'internet support, ISP help, South Africa internet troubleshooting, Supersonic support, customer service',

                    ogTitle: 'Supersonic Support - Help & Resources for Your Internet Services in South Africa',
                    ogDescription: 'Get comprehensive support with Supersonic. Access FAQs, troubleshooting tips, and contact options for assistance with your internet services. We’re here to help you with all your connectivity needs.',
                    ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    ogUrl: 'https://supersonic.co.za/support',
                    ogType: 'website',

                    twCard: 'summary_large_image',
                    twTitle: 'Supersonic Support - Help & Resources for Your Internet Services in South Africa',
                    twDescription: 'Get comprehensive support with Supersonic. Access FAQs, troubleshooting tips, and contact options for assistance with your internet services. We’re here to help you with all your connectivity needs.',
                    twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',

                    canonical: 'https://supersonic.co.za/support'
                }
            },
            //{path: 'network', loadChildren: () => import('app/modules/network-status/network-status.routes')},
            //{path: 'blogs', loadChildren: () => import('app/modules/blogs/blogs.routes')},
            {path: 'pre-orders', loadComponent: () => import('app/modules/sales-agent/sales-agent.component').then(comp => comp.SalesAgentComponent)},
            {
                path: 'home', loadComponent: () => import('app/modules/landing/home/home.component').then(comp => comp.LandingHomeComponent),
                data: {
                    title: 'Supersonic - Leading ISP in South Africa | High-Speed Internet Solutions',
                    description: 'Supersonic offers high-speed internet solutions across South Africa. Discover our affordable plans and reliable connectivity options for home and business.',
                    keywords: 'Supersonic, high-speed internet South Africa, reliable ISP, affordable internet plans, home internet, business internet, internet deals, uncapped fibre, coverage check, Supersonic login',

                    ogTitle: 'Supersonic - Leading ISP in South Africa | High-Speed Internet Solutions',
                    ogDescription: 'Supersonic offers high-speed internet solutions across South Africa. Discover our affordable plans and reliable connectivity options for home and business.',
                    ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    ogUrl: 'https://supersonic.co.za',
                    ogType: 'website',

                    twCard: 'summary_large_image',
                    twTitle: 'Supersonic - Leading ISP in South Africa | High-Speed Internet Solutions',
                    twDescription: 'Supersonic offers high-speed internet solutions across South Africa. Discover our affordable plans and reliable connectivity options for home and business.',
                    twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',

                    canonical: 'https://supersonicv2.netlify'
                }
            },
            {path: 'packages', loadComponent: () => import('app/modules/order-process/packages/packages.component').then(comp => comp.PackagesComponent)},
            {path: 'balances', loadComponent: () => import('app/modules/outstanding/outstanding.component').then(comp => comp.OutstandingComponent)},
            {path: 'cart', loadComponent: () => import('app/modules/order-process/cart/cart.component').then(comp => comp.CartComponent)},
            {path: 'confirm-order', loadComponent: () => import('app/modules/order-process/confirm-order/confirm-order.component').then(comp => comp.ConfirmOrderComponent)},
            {
                path: 'deals', loadComponent: () => import('app/modules/deals/deals.component').then(comp => comp.DealsComponent),
                data: {
                    title: 'Supersonic Internet Deals - Best Discounts & Promotions in South Africa',
                    description: 'Discover the latest deals and promotions from Supersonic’s. Get the best discounts on high-speed internet services across South Africa with our exclusive offers.',
                    keywords: 'internet deals, ISP promotions, South Africa broadband discounts, Supersonic offers, internet plan deals',

                    ogTitle: 'Supersonic Internet Deals - Best Discounts & Promotions in South Africa',
                    ogDescription: 'Discover the latest deals and promotions from Supersonic’s. Get the best discounts on high-speed internet services across South Africa with our exclusive offers.',
                    ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    ogUrl: 'https://supersonic.co.za/deals',
                    ogType: 'website',

                    twCard: 'summary_large_image',
                    twTitle: 'Supersonic Internet Deals - Best Discounts & Promotions in South Africa',
                    twDescription: 'Discover the latest deals and promotions from Supersonic’s. Get the best discounts on high-speed internet services across South Africa with our exclusive offers.',
                    twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',

                    canonical: 'https://supersonic.co.za/deals'
                }
            },
        ]
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        // canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            // {path: 'example', loadChildren: () => import('app/modules/admin/example/example.routes')},
            {path: 'dashboard', loadComponent: () => import('app/modules/dashboard/dashboard.component').then(comp => comp.DashboardComponent)},
            // {path: 'dashboard/services', loadComponent: () => import('app/modules/dashboard/services/services.routes')},
            // {path: 'dashboard/tickets', loadComponent: () => import('app/modules/dashboard/tickets/tickets.routes')},
            // {path: 'dashboard/payment-method', loadComponent: () => import('app/modules/shared-components/payment-method/payment-methods.routes')},
            {path: 'dashboard/profile', loadComponent: () => import('app/modules/dashboard/profile/profile.component').then(comp => comp.ProfileComponent)},
            // {path: 'dashboard/invoiceStatements', loadComponent: () => import('app/modules/dashboard/invoice-statements/invoice-statements.routes')},
            {path: 'dashboard/requests', loadComponent: () => import('app/modules/dashboard/requests/requests.component').then(comp => comp.RequestsComponent)},
            {path: 'dashboard/invoices', loadComponent: () => import('app/modules/dashboard/invoices/invoices.component').then(comp => comp.InvoicesComponent)},
        ]
    },

   

    // 404
    {path: '**', pathMatch: 'full', component: NotFoundComponent},
]